'use client';

import React, { useEffect, useState } from 'react';
import { ShieldCheck, AlertTriangle, FileText } from 'lucide-react';

const TERMS_KEY = 'directly_terms_accepted';

export default function TermsModal() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    const accepted = localStorage.getItem(TERMS_KEY);
    if (!accepted) {
      setOpen(true);
    }
  }, []);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const atBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 40;
    if (atBottom) setScrolled(true);
  };

  const handleAccept = () => {
    if (!agreed) return;
    localStorage.setItem(TERMS_KEY, 'true');
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-l from-blue-700 to-green-600 px-6 py-5 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 rounded-full p-2">
              <ShieldCheck size={24} className="text-white" />
            </div>
            <div>
              <h2 className="text-white font-800 text-lg">الشروط والأحكام — Directly</h2>
              <p className="text-white/80 text-xs mt-0.5">يرجى قراءة الشروط كاملاً قبل المتابعة</p>
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div
          className="flex-1 overflow-y-auto px-6 py-5 text-right"
          dir="rtl"
          onScroll={handleScroll}
        >
          {/* Marketing Section */}
          <div className="bg-gradient-to-l from-blue-50 to-green-50 border border-blue-100 rounded-xl p-5 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl">☕</span>
              <h3 className="font-800 text-blue-800 text-base">تواصل مع البائع مباشرة — بدون وسيط وبدون عمولة!</h3>
            </div>
            <p className="text-gray-700 text-sm leading-relaxed mb-3">
              في Directly، نؤمن بأن شراء أو استئجار العقار يجب أن يكون بسيطاً وشفافاً. لا وسطاء، لا عمولات مخفية، لا رسوم وكالة باهظة.
            </p>
            <p className="text-gray-700 text-sm leading-relaxed mb-3">
              بمجرد أن تجد العقار المناسب، يمكنك الكشف عن بيانات التواصل المباشرة مع المالك بتكلفة رمزية — أقل من ثمن فنجان قهوة — وتتواصل معه مباشرة دون أي وسيط.
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              {['✅ بدون عمولة وكيل', '✅ تواصل مباشر مع المالك', '✅ توفير آلاف الدراهم/الريالات', '✅ شفافية كاملة'].map(item => (
                <span key={item} className="bg-white border border-green-200 text-green-800 text-xs font-600 px-3 py-1 rounded-full">{item}</span>
              ))}
            </div>
          </div>

          {/* Warning */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5 flex gap-3">
            <AlertTriangle size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-amber-800 text-sm leading-relaxed">
              <strong>تنبيه مهم:</strong> Directly منصة لعرض الإعلانات العقارية فقط. نحن لا نبيع عقارات ولا نمثل أي طرف في أي صفقة. كل مستخدم مسؤول مسؤولية كاملة عن التحقق من صحة المعلومات والتعامل بحذر.
            </p>
          </div>

          {/* Legal Disclaimer */}
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-3">
              <FileText size={18} className="text-blue-600" />
              <h3 className="font-700 text-gray-800 text-base">إخلاء المسؤولية القانونية الكاملة</h3>
            </div>

            <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
              <div className="border-r-4 border-blue-500 pr-4">
                <h4 className="font-700 text-gray-800 mb-1">١. طبيعة الخدمة</h4>
                <p>Directly هي منصة إلكترونية لعرض الإعلانات العقارية فقط. تقوم المنصة بعرض البيانات التي يقدمها المعلنون دون أي تحقق من صحتها أو ضمان دقتها. لا تعمل Directly كوكيل عقاري أو وسيط أو مستشار قانوني أو مالي.</p>
              </div>

              <div className="border-r-4 border-green-500 pr-4">
                <h4 className="font-700 text-gray-800 mb-1">٢. إخلاء المسؤولية عن الصفقات</h4>
                <p>Directly غير مسؤولة بأي شكل من الأشكال عن أي صفقة تتم بين المستخدمين، سواء كانت عمليات بيع أو شراء أو إيجار أو أي نوع آخر من التعاملات العقارية. أي نزاع أو خسارة أو احتيال أو ضرر ينشأ عن التعاملات بين المستخدمين هو مسؤولية الأطراف المعنية حصراً.</p>
              </div>

              <div className="border-r-4 border-purple-500 pr-4">
                <h4 className="font-700 text-gray-800 mb-1">٣. مسؤولية المستخدم</h4>
                <p>يتحمل كل مستخدم المسؤولية الكاملة عن: التحقق من هوية الطرف الآخر، التحقق من ملكية العقار والوثائق القانونية، الاستعانة بمحامٍ أو مستشار قانوني قبل إتمام أي صفقة، وعدم تحويل أي مبالغ مالية دون التحقق الكامل.</p>
              </div>

              <div className="border-r-4 border-red-500 pr-4">
                <h4 className="font-700 text-gray-800 mb-1">٤. الاحتيال والنصب</h4>
                <p>Directly لا تضمن صحة أي إعلان أو هوية أي مستخدم. في حال تعرضك لأي عملية احتيال، يجب عليك التواصل مع الجهات القانونية المختصة في بلدك مباشرةً. لا يمكن مطالبة Directly بأي تعويض مالي أو قانوني في هذا الشأن.</p>
              </div>

              <div className="border-r-4 border-orange-500 pr-4">
                <h4 className="font-700 text-gray-800 mb-1">٥. دقة المعلومات</h4>
                <p>جميع المعلومات المعروضة على المنصة مقدمة من المعلنين أنفسهم. Directly لا تتحقق من صحة الأسعار أو المساحات أو الأوصاف أو الصور. يتحمل المعلن المسؤولية الكاملة عن دقة ما ينشره.</p>
              </div>

              <div className="border-r-4 border-gray-400 pr-4">
                <h4 className="font-700 text-gray-800 mb-1">٦. تعديل الشروط</h4>
                <p>تحتفظ Directly بحق تعديل هذه الشروط في أي وقت. استمرارك في استخدام المنصة بعد نشر التعديلات يعني موافقتك الضمنية عليها.</p>
              </div>
            </div>
          </div>

          {/* Scroll hint */}
          {!scrolled && (
            <p className="text-center text-xs text-gray-400 animate-bounce mt-2">↓ مرر للأسفل لقراءة كامل الشروط</p>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 px-6 py-4 flex-shrink-0 bg-gray-50">
          <label className="flex items-start gap-3 cursor-pointer mb-4" dir="rtl">
            <input
              type="checkbox"
              checked={agreed}
              onChange={e => setAgreed(e.target.checked)}
              className="mt-1 w-4 h-4 accent-blue-600 cursor-pointer flex-shrink-0"
            />
            <span className="text-sm text-gray-700 leading-relaxed">
              أقر بأنني قرأت وفهمت الشروط والأحكام كاملاً، وأوافق على أن Directly منصة عرض إعلانات فقط وغير مسؤولة عن أي صفقات أو نزاعات بين المستخدمين.
            </span>
          </label>
          <button
            onClick={handleAccept}
            disabled={!agreed}
            className={`w-full py-3 rounded-xl font-700 text-sm transition-all duration-200 ${
              agreed
                ? 'bg-gradient-to-l from-blue-700 to-green-600 text-white hover:opacity-90 shadow-md'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {agreed ? '✅ أوافق وأدخل الموقع' : 'يجب الموافقة على الشروط للمتابعة'}
          </button>
        </div>
      </div>
    </div>
  );
}
